import { useEffect } from "react";
import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Loader2 } from "lucide-react";
import { useGetAllImporters } from "@/api/importer.api";
import { ControlledSelectV2 } from "@/components/ui/customs/controlled-select-v2";
import { QuotationUpdateBoFormData } from "@/types/forms/bo/expeditionBo.form";

type ImporterSelectProps = {
  originalImporterId?: string;
  readOnly?: boolean;
};

// Sometime there is a need to change from the default importer for a client
// In such case we can sue a select. Because one expedition can only have one importer
// we need to reset already filled productUSAId
export const ImporterSelect = ({ originalImporterId, readOnly = false }: ImporterSelectProps) => {
  const { t } = useTranslation("boExpeditionForm");
  const { control, watch, getValues, setValue } = useFormContext<QuotationUpdateBoFormData>();
  const { data: importers, isLoading } = useGetAllImporters();

  const options =
    importers?.data?.map((importer) => ({
      label: importer.name,
      value: importer.id,
    })) ?? [];

  const importerId = watch("importerId");

  useEffect(() => {
    if (readOnly || !importerId || importerId === originalImporterId) return;

    const deliveryContent = getValues("deliveryContent");
    deliveryContent?.forEach((item, index) => {
      if (item.productUSAId) {
        setValue(`deliveryContent.${index}.productUSAId`, undefined as any, {
          shouldDirty: true,
          shouldTouch: true,
        });
      }
    });
  }, [readOnly, importerId, originalImporterId, getValues, setValue]);

  return (
    <div className="flex w-full flex-col gap-1 rounded-md bg-gray-100 p-2">
      <div className="flex flex-row items-center gap-2">
        <h4 className="text-base font-bold text-gray-900">{t("importerSelect.title")}</h4>
      </div>
      {isLoading ? (
        <div className="flex items-center justify-center py-2">
          <Loader2 className="h-4 w-4 animate-spin text-gray-500" />
        </div>
      ) : readOnly ? (
        <p className="text-sm text-gray-900">
          {options.find((option) => option.value === originalImporterId)?.label ?? "-"}
        </p>
      ) : (
        <ControlledSelectV2
          control={control}
          name="importerId"
          options={options}
          placeholder={t("importerSelect.placeholder")}
          searchable
          searchPlaceholder={t("importerSelect.searchPlaceholder")}
          contentClassName="bg-white"
        />
      )}
    </div>
  );
};
